import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import type { ChipFilterValue } from '@openx/types/chipFilters.ts';
import { type HTMLAttributes, type JSX, useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type NumberFilterData, NumberFilterFields, RangeOptions } from './types.ts';
import { getOptionParam } from './utils.ts';

const onKeyDown = e => {
  if (e.target.type !== 'textarea' && e.key === 'Enter') {
    // disable submit form on enter
    e.preventDefault();
  }
};

type NumberFilterInputsProps = {
  setCurrentOption: (val: ChipFilterValue) => void;
  setError: (val: boolean) => void;
  currencySymbol?: string;
};

export const NumberFilterInputs = ({
  setCurrentOption,
  currencySymbol = '',
  setError,
}: NumberFilterInputsProps): JSX.Element => {
  const {
    setValue,
    control,
    formState: { isValid },
  } = useFormContext<NumberFilterData>();
  const { t } = useTranslation();
  const observableOptionValue = useWatch({
    control,
    name: NumberFilterFields.OPTION,
  });
  const observableValue = useWatch({
    control,
    name: NumberFilterFields.VALUE,
  });
  const observableValueTo = useWatch({
    control,
    name: NumberFilterFields.VALUE_TO,
  });

  const isRangeOption = observableOptionValue === RangeOptions.RANGE;

  useEffect(() => {
    setCurrentOption(getOptionParam(observableOptionValue, observableValue, observableValueTo));
    setError(!isValid || observableValue === '');
  }, [observableOptionValue, observableValue, observableValueTo, setCurrentOption, setError, isValid]);

  const labels = useMemo(
    () => ({
      [RangeOptions.GT]: t('Greater than'),
      [RangeOptions.LT]: t('Less than'),
      [RangeOptions.RANGE]: t('In range'),
      [RangeOptions.EQUAL]: t('Equal to'),
    }),
    [t]
  );

  const clearAllValues = useCallback(() => {
    setValue(NumberFilterFields.VALUE, '');
    setValue(NumberFilterFields.VALUE_TO, '');
  }, [setValue]);

  const onRangeSelectChange = useCallback(
    (_, option: RangeOptions | null) => {
      clearAllValues();
      setValue(NumberFilterFields.OPTION, option);
    },
    [clearAllValues, setValue]
  );

  return (
    <form data-test="ad-units-form" autoComplete="off" onKeyDown={onKeyDown} noValidate>
      <RHFAutoComplete
        name={NumberFilterFields.OPTION}
        textFieldProps={{ placeholder: t('Select option') }}
        getOptionLabel={option => labels[option]}
        options={Object.keys(RangeOptions) as RangeOptions[]}
        slotProps={{ listbox: { 'data-test': 'list-of-items' } as HTMLAttributes<HTMLUListElement> }}
        data-test="filter-list"
        onChange={onRangeSelectChange}
      />
      <Grid container spacing={1}>
        {observableOptionValue && (
          <Grid>
            <RHFTextField
              name={NumberFilterFields.VALUE}
              placeholder={isRangeOption ? t('From') : t('Value')}
              dataTest="chip-filter-number-value-input"
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                },
              }}
              variant="outlined"
              margin="dense"
              fullWidth={!isRangeOption}
            />
          </Grid>
        )}
        {isRangeOption && (
          <Grid>
            <RHFTextField
              name={NumberFilterFields.VALUE_TO}
              placeholder={t('To')}
              dataTest="chip-filter-number-value-input"
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                },
              }}
              variant="outlined"
              margin="dense"
            />
          </Grid>
        )}
      </Grid>
    </form>
  );
};
